import { HttpClient } from '@sisense/sdk-rest-client';
import { ALLOWED_HEADERS } from '@/constants';
import { getDefaultHttpHeaders } from './http-client';
import { resolveTrustedUrl } from './trusted-base-url';
import { getUserActionHeaders } from './user-action-tracking';

/**
 * Factory function to create a custom fetch function for OpenAI chat completions
 * This fetch function applies authentication headers from the provided httpClient
 * and sets the user action tracking headers.
 * @param httpClient - The HTTP client instance to use for authentication
 * @returns Custom fetch function
 */
export function createCustomFetch(httpClient: HttpClient) {
	return async (
		url: RequestInfo | URL,
		init?: RequestInit,
	): Promise<Response> => {
		const rawUrl = url instanceof Request ? url.url : url.toString();
		let resolvedUrl: URL;
		try {
			resolvedUrl = resolveTrustedUrl(httpClient.url, rawUrl);
		} catch {
			throw new Error('Use httpClient for non-completion requests');
		}
		if (!/(^|\/)chat\/completions(\/|$)/.test(resolvedUrl.pathname)) {
			throw new Error('Use httpClient for non-completion requests');
		}

		const headers = new Headers();
		const isBearerAuth = httpClient.auth.type === 'bearer';

		if (init?.headers) {
			const originalHeaders = new Headers(init.headers);
			originalHeaders.forEach((value, key) => {
				const lowerKey = key.toLowerCase();
				// Skip tracking headers - we set them from getUserActionHeaders()
				if (ALLOWED_HEADERS.includes(lowerKey)) return;
				// Sisense server doesn't allow x- headers except tracking
				if (lowerKey.startsWith('x-')) return;
				// httpClient sets its own authorization header (unless bearer auth)
				if (!isBearerAuth && lowerKey === 'authorization') return;
				headers.set(key, value);
			});
		}

		const trackingHeaders = getUserActionHeaders();
		ALLOWED_HEADERS.forEach((header) => {
			headers.set(header, trackingHeaders[header]);
		});
		Object.entries(getDefaultHttpHeaders()).forEach(([key, value]) => {
			headers.set(key, value);
		});

		httpClient.auth.applyHeaders(headers);
		const newInit = { ...(init || {}), headers };
		return fetch(resolvedUrl.toString(), newInit);
	};
}
